import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import TableShimmer from "../../UI/Shimmer/TableShimmer";
import AgGridWrapper from "../../UI/AgGrid";
import { getProductFeedRunById, getProductFeedRuns, runProductFeed } from "../../../utils/APIs";
import { GridHeader, optionsTip, outcomesTip, OutcomeBadge, Section, Stat } from "./common";
import { GLOSSARY } from "./glossary";
import { compareCells, errorMessage, formatBytes, formatDate, formatDuration, toTime } from "./helpers";
import { SOURCE_STATS_TIP, SourceStatsTable } from "./PreviewTab";
import { PushResult } from "./DeliverySection";

const POLL_MS = 5000;

const OUTCOME_TIP = outcomesTip();
const RUN_ACTIONS_TIP = optionsTip(
    undefined,
    { details: "Sources, guardrails, column fill and issues for the build.", rollback: GLOSSARY.stats.rollback },
    { details: "Details", rollback: "Roll back" }
);

const PUSH_TIP = optionsTip("For feeds delivered through the Adobe Target API: how the build's push went.", GLOSSARY.delivery.pushStatus);

const header = (tip) => ({ headerComponent: GridHeader, headerComponentParams: { tip } });

// Runs tab: run history and run detail. Stride keeps no feed files, so
// download and rollback are shown but unavailable.
const RunsTab = ({ feed, auth, feedId, reload }) => {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [detail, setDetail] = useState(null);

    const loadRuns = async () => {
        try {
            const result = await getProductFeedRuns({ ...auth, feedId, limit: 100 });
            setRuns(result?.data || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRuns();
    }, [feedId]);

    // Keep refreshing while a run is in progress. The editor already refreshes
    // the feed while it shows activity.
    const inProgress = running || !!feed.activity || runs.some((r) => r.outcome === "running");
    useEffect(() => {
        if (!inProgress) return undefined;
        const timer = setInterval(() => {
            loadRuns();
            if (!feed.activity) reload();
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [inProgress, !!feed.activity]);

    // One last refresh when the feed's build finishes, so its row is final.
    const wasActive = useRef(false);
    useEffect(() => {
        if (wasActive.current && !feed.activity) loadRuns();
        wasActive.current = !!feed.activity;
    }, [!!feed.activity]);

    const runNow = async () => {
        try {
            setRunning(true);
            toast.info("Build started. This can take a minute for large feeds.");
            const result = await runProductFeed({ ...auth, feedId });
            const outcome = result?.data?.outcome;
            if (outcome === "running") toast.info("The build is still running; this list refreshes automatically.");
            else if (outcome === "failed" || outcome === "held") toast.error(`Build ${outcome}: ${result?.data?.message || ""}`);
            else toast.success(`Build ${outcome}`);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setRunning(false);
            loadRuns();
            reload();
        }
    };

    const openDetail = async (run) => {
        try {
            const result = await getProductFeedRunById({ ...auth, feedId, runId: run._id });
            setDetail(result?.data);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const columnDefs = useMemo(() => [
        { headerName: "Started", flex: 1.3, valueGetter: ({ data }) => toTime(data.started_at), valueFormatter: ({ value }) => formatDate(value), comparator: compareCells },
        { headerName: "Trigger", field: "trigger", flex: 0.8, ...header(GLOSSARY.stats.trigger) },
        { headerName: "Outcome", field: "outcome", flex: 0.9, ...header(OUTCOME_TIP), cellRenderer: ({ value }) => <div className="flex items-center h-full"><OutcomeBadge outcome={value} /></div> },
        {
            headerName: "Target push",
            flex: 0.9,
            valueGetter: ({ data }) => data.details?.push?.status || "",
            tooltipValueGetter: ({ data }) => data.details?.push?.message,
            ...header(PUSH_TIP),
            cellRenderer: ({ value }) => <div className="flex items-center h-full">{value ? <OutcomeBadge outcome={value} /> : <span className="text-gray-400">—</span>}</div>,
        },
        { headerName: "Products", flex: 0.8, valueGetter: ({ data }) => data.details?.rows?.live ?? null, valueFormatter: ({ value }) => value ?? "—", comparator: compareCells, ...header(GLOSSARY.stats.products) },
        { headerName: "Duration", flex: 0.8, valueGetter: ({ data }) => data.duration_ms ?? null, valueFormatter: ({ value }) => formatDuration(value), comparator: compareCells, ...header(GLOSSARY.stats.duration) },
        { headerName: "Config", flex: 0.6, valueGetter: ({ data }) => data.config_version || null, valueFormatter: ({ value }) => (value ? `v${value}` : "—"), comparator: compareCells, ...header(GLOSSARY.stats.configVersion) },
        { headerName: "Message", field: "message", flex: 2.5, tooltipField: "message" },
        {
            headerName: "",
            field: "actions",
            width: 190,
            sortable: false,
            ...header(RUN_ACTIONS_TIP),
            cellRenderer: ({ data }) => (
                <div className="flex items-center gap-3 h-full text-sm">
                    <button type="button" className="text-primary hover:underline" onClick={() => openDetail(data)}>Details</button>
                    {data.outcome === "published" && (
                        <button type="button" disabled title={GLOSSARY.stats.rollback} className="text-gray-400 cursor-not-allowed">Roll back</button>
                    )}
                </div>
            ),
        },
    ], [runs]);

    return (
        <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton size="sm" loading={running} disable={!feed.is_published || inProgress} handleClick={runNow}>Run now</PrimaryButton>
                <span title={GLOSSARY.stats.download}>
                    <SecondaryButton size="sm" disable>Download current CSV</SecondaryButton>
                </span>
                {!feed.is_published && <span className="text-sm text-gray-500">Publish the feed to start building it.</span>}
            </div>

            {loading ? (
                <TableShimmer colSize={7} rowSize={4} />
            ) : runs.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">No runs yet.</p>
            ) : (
                <div className="ag-theme-quartz" style={{ width: "100%" }}>
                    <AgGridWrapper tableId="product-feed-runs" rowData={runs} columnDefs={columnDefs} defaultColDef={{ resizable: true, sortable: true }} tooltipShowDelay={300} />
                </div>
            )}

            {detail && (
                <PrimaryModal title={`Run ${formatDate(detail.started_at)}`} hideModal={() => setDetail(null)} widthClass="max-w-6xl">
                    <div className="p-6 flex flex-col gap-4 max-h-[75vh] overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                            <Stat label="Outcome" value={<OutcomeBadge outcome={detail.outcome} />} tip={OUTCOME_TIP} />
                            <Stat label="Trigger" value={detail.trigger} tip={GLOSSARY.stats.trigger} />
                            <Stat label="Products" value={detail.details?.rows?.live} tip={GLOSSARY.stats.products} />
                            <Stat label="Invalid rows" value={detail.details?.rows?.invalid} tip={GLOSSARY.stats.invalid} />
                            <Stat label="File" value={formatBytes(detail.details?.file?.bytes)} tip={GLOSSARY.stats.fileSize} />
                            <Stat label="Duration" value={formatDuration(detail.duration_ms)} tip={GLOSSARY.stats.duration} />
                        </div>
                        {detail.message && <p className="text-sm text-gray-700">{detail.message}</p>}
                        {(detail.details?.messages || []).map((m, i) => (
                            <p key={i} className={`text-sm ${m.level === "error" ? "text-red-600" : "text-yellow-700"}`}>{m.message}</p>
                        ))}
                        {detail.details?.push && (
                            <Section title="Adobe Target push" tip={PUSH_TIP}>
                                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">Status <OutcomeBadge outcome={detail.details.push.status} /></span>
                                    {detail.details.push.tenant && <span>Tenant {detail.details.push.tenant}</span>}
                                    {detail.details.push.environment_id && <span>Environment {detail.details.push.environment_id}</span>}
                                    {detail.details.push.duration_ms !== undefined && <span>Took {formatDuration(detail.details.push.duration_ms)}</span>}
                                </div>
                                <PushResult push={detail.details.push} />
                            </Section>
                        )}
                        {detail.details?.sources && (
                            <Section title="Sources" tip={SOURCE_STATS_TIP}>
                                <SourceStatsTable sources={detail.details.sources} />
                            </Section>
                        )}
                        {detail.details?.guardrails && (
                            <Section title="Guardrails" tip={GLOSSARY.stats.guardrails}>
                                {detail.details.guardrails.checks.map((check) => (
                                    <p key={check.name} className={`text-sm ${check.ok ? "text-gray-600" : "text-red-600"}`}>{check.ok ? "✓" : "✗"} {check.detail}</p>
                                ))}
                            </Section>
                        )}
                        {detail.details?.columns?.fill && (
                            <Section title="Column fill rate" tip={GLOSSARY.stats.fill}>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(detail.details.columns.fill).map(([name, count]) => (
                                        <span key={name} className="text-xs px-2 py-0.5 rounded bg-gray-100 font-mono">
                                            {name}: {detail.details.rows?.live ? Math.round((count / detail.details.rows.live) * 100) : 0}%
                                        </span>
                                    ))}
                                </div>
                            </Section>
                        )}
                        {((detail.details?.issues || []).length > 0 || (detail.details?.invalid || []).length > 0) && (
                            <Section title="Issues" tip={GLOSSARY.stats.issues}>
                                {(detail.details.issues || []).map((issue, i) => (
                                    <p key={i} className="text-sm text-yellow-700">{issue.message} <span className="text-gray-500">({issue.count} rows)</span></p>
                                ))}
                                {(detail.details.invalid || []).map((row, i) => (
                                    <p key={`inv${i}`} className="text-sm text-red-600"><span className="font-mono">{row.key}</span> dropped: {row.reason}</p>
                                ))}
                            </Section>
                        )}
                    </div>
                </PrimaryModal>
            )}

        </div>
    );
};

export default RunsTab;
